import { getWorkspaceIdFromCurrentUrl, useCurrentWorkspace } from '@/context/current-workspace';
import { Sidebar, toggle } from '@/features/sidebar';
import { IconButton } from '@/layouts/authenticated/components/top-navigation/components/icon-button';
import { servicesApi } from '@/services/api';
import { servicesApi as untypedServicesApi } from '@/services/api/deprecated';
import { sentryCaptureException } from '@/utils/sentry';
import type { CopilotSession, CopilotStatefulMessage, CopilotStatefulToolCall } from '@customerio/hydra-copilot';
import { setCopilotConfig } from '@customerio/hydra-copilot';
import {
  useCopilotAvailableTools,
  useCopilotMessages,
  useCopilotSession,
  useCopilotStatus,
  CopilotProvider,
  Markdown,
} from '@customerio/hydra-copilot/react';
import type { MDXComponents } from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  Box,
  Button,
  Code,
  Divider,
  Emphasis,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Form,
  Heading,
  Icon,
  Image,
  InlineStack,
  Label,
  Link,
  List,
  ListItem,
  Paragraph,
  Popover,
  PopoverContent,
  Scrollable,
  Spinner,
  Stack,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  TextArea,
  Tooltip,
  useSnackbars,
} from '@customerio/pluma-components/react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { lowerCase, upperFirst } from 'lodash-es';
import plur from 'plur';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import * as styles from './index.css';
import { ShimmerText } from './shimmer-text';
import { ToastSuggestions } from './toast-suggestions';

setCopilotConfig({
  async startSession() {
    const workspaceId = getWorkspaceIdFromCurrentUrl();
    if (workspaceId == null) {
      throw new Error('No workspace ID found in the current URL');
    }

    const response = await servicesApi.startCopilotSession({
      params: { workspaceId: Number(workspaceId) },
      body: {},
    });

    return {
      id: `${workspaceId}:${response.session_id}`,
      tools: response.tools,
    };
  },
  async getSessionMessages(id) {
    const [workspaceId, sessionId] = id.split(':');
    const response = await servicesApi.getCopilotSessionMessages({
      params: { workspaceId: Number(workspaceId), sessionId },
    });

    return response.messages;
  },
  async sendStreamingMessage({ onMessage, id, ...payload }) {
    const [workspaceId, sessionId] = id.split(':');

    let currentLine = -1;
    await untypedServicesApi.post(
      `/environments/${workspaceId}/copilot/${sessionId}`,
      {
        stream: true,
        ...payload,
      },
      {
        onDownloadProgress: (progressEvent) => {
          const response = progressEvent.event.currentTarget.response;
          const lines = response.split('\n');

          for (let index = 0; index < lines.length; index++) {
            const line = lines[index].trim();
            if (line.length === 0) {
              continue;
            }

            // skip lines that have already been processed
            if (index <= currentLine) {
              continue;
            }

            // we are now processing this line
            currentLine = index;

            try {
              onMessage(JSON.parse(line));
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error parsing message from server:', line, error);
              // TODO: If we can't parse the message, we should kill this exchange entirely
              // This can happen if the server sends an incomplete message
            }
          }
        },
      },
    );
  },
  async destroySession(id) {
    const [workspaceId, sessionId] = id.split(':');
    await servicesApi.destroyCopilotSession({ params: { workspaceId: Number(workspaceId), sessionId } });
  },
  async generate(payload) {
    const workspaceId = getWorkspaceIdFromCurrentUrl();
    if (workspaceId == null) {
      throw new Error('No workspace ID found in the current URL');
    }

    const { data } = await untypedServicesApi.post(`/environments/${workspaceId}/copilot_generate`, payload);

    return data;
  },
});

const components: MDXComponents = {
  p: ({ children }) => <Paragraph style={{ lineHeight: 1.65 }}>{children}</Paragraph>,
  h1: ({ children }) => <Heading level="2">{children}</Heading>,
  h2: ({ children }) => <Heading level="3">{children}</Heading>,
  h3: ({ children }) => <Heading level="4">{children}</Heading>,
  h4: ({ children }) => <Heading level="5">{children}</Heading>,
  h5: ({ children }) => <Heading level="6">{children}</Heading>,
  h6: ({ children }) => <Heading level="6">{children}</Heading>,
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Emphasis>{children}</Emphasis>,
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const language = typeof className === 'string' ? className.replace('language-', '') : '';

    const isMultiline = typeof children === 'string' && children.includes('\n');

    return language.length > 0 || isMultiline ? (
      <Box
        p="150"
        backgroundColor="subtle"
        style={{
          borderRadius: themeVars.radius['6'],
          overflowX: 'auto',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="100">
          <Text size="xs" color="subtle" weight="medium">
            {language ?? 'plaintext'}
          </Text>
        </Box>
        <Text as="pre" style={{ margin: 0, maxWidth: '100%', overflowX: 'auto' }}>
          <Text
            as="code"
            style={{
              whiteSpace: 'pre',
              background: 'transparent',
              fontFamily: themeVars['font-family']['mono'],
              fontWeight: 'normal',
            }}
          >
            {children}
          </Text>
        </Text>
      </Box>
    ) : (
      <Code>{children}</Code>
    );
  },
  blockquote: ({ children }) => (
    <Box
      pl="200"
      as="blockquote"
      style={{
        borderLeft: `4px solid ${themeVars.color['border-minimal']}`,
        marginLeft: 0,
        marginRight: 0,
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }) => <List type="unordered">{children}</List>,
  ol: ({ children }) => <List type="ordered">{children}</List>,
  li: ({ children }) => <ListItem>{children}</ListItem>,
  a: ({ href, children }) => (
    <Link href={href} isExternal={true} variant="secondary">
      {children}
    </Link>
  ),
  img: ({ src, alt }) => <Image src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />,
  hr: () => <Divider my="100" />,
  table: ({ children }) => (
    <Box overflowX="auto">
      <Table caption="Table">{children}</Table>
    </Box>
  ),
  thead: ({ children }) => <TableThead>{children}</TableThead>,
  tbody: ({ children }) => <TableTbody>{children}</TableTbody>,
  tr: ({ children }) => <TableTr>{children}</TableTr>,
  th: ({ children }) => <TableTh>{children}</TableTh>,
  td: ({ children }) => <TableTd>{children}</TableTd>,
};

export function Copilot() {
  const workspace = useCurrentWorkspace();
  const session = useCopilotSession({ shouldInitialize: false });
  const messages = useCopilotMessages(session);
  const status = useCopilotStatus(session);
  const isSending = status === 'sending';
  const messagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesRef.current == null) {
      return;
    }

    messagesRef.current.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  if (workspace == null) {
    return null;
  }

  return (
    <>
      <IconButton
        label="AI Copilot"
        icon="sparkle"
        onClick={() => {
          toggle('copilot');
          session.initialize();
        }}
      />
      <Sidebar id="copilot">
        <CopilotProvider components={components}>
          <Box display="flex" flexDirection="column" backgroundColor="base" h="full" w="full">
            <Box display="flex" alignItems="center" justifyContent="space-between" py="100" pl="200" pr="100">
              <Heading level="3">Copilot</Heading>
              <Box display="flex" alignItems="center">
                <Button
                  isIconOnly
                  icon="add"
                  onClick={() => {
                    const newSession = session.reset();
                    newSession.initialize();
                  }}
                  variant="subtle"
                />
                <Button
                  isIconOnly
                  icon="close"
                  onClick={() => {
                    toggle('copilot');
                  }}
                  variant="subtle"
                />
              </Box>
            </Box>
            <Divider />
            <Box flexGrow="1" h="0" display="flex" flexDirection="column">
              {status === 'error' ? (
                <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
                  <EmptyState>
                    <EmptyStateTitle icon="error">Failed to load</EmptyStateTitle>
                    <EmptyStateDescription>There was an error loading Copilot. Please try again.</EmptyStateDescription>
                    <EmptyStateActions>
                      <Button
                        variant="primary"
                        onClick={() => {
                          const newSession = session.reset();
                          newSession.initialize();
                        }}
                      >
                        Retry
                      </Button>
                    </EmptyStateActions>
                  </EmptyState>
                </Box>
              ) : (
                <>
                  <Stack overflowY="auto" ref={messagesRef} h="0" flexGrow="1" gap="150" px="150" py="200">
                    {messages.length === 0 ? (
                      <Box flexGrow="1" display="flex" justifyContent="center" alignItems="center">
                        <EmptyState>
                          <EmptyStateTitle icon="chat">No messages yet</EmptyStateTitle>
                          <EmptyStateDescription>Start a conversation with Copilot</EmptyStateDescription>
                        </EmptyState>
                      </Box>
                    ) : (
                      <>
                        {messages.map((message, index) => (
                          <Message
                            key={index}
                            message={message}
                            isLastMessage={!isSending && index === messages.length - 1}
                          />
                        ))}
                        {isSending ? (
                          <AnimateMessage>
                            <Box my="100" px="125">
                              <ShimmerText>Thinking&hellip;</ShimmerText>
                            </Box>
                          </AnimateMessage>
                        ) : null}
                      </>
                    )}
                  </Stack>
                  <Input session={session} />
                </>
              )}
            </Box>
          </Box>
        </CopilotProvider>
      </Sidebar>
      <ToastSuggestions session={session} />
    </>
  );
}

function MessageActions({ text }: { text: string }) {
  const snackbar = useSnackbars();

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(text);
    snackbar.create({
      message: 'Copied to clipboard',
    });
  };

  const handleThumbsUp = () => {
    // Handle thumbs up action - can add feedback API call here
    snackbar.create({
      message: 'Thank you for your feedback!',
    });
  };

  const handleThumbsDown = () => {
    // Handle thumbs down action - can add feedback API call here
    snackbar.create({
      message: 'Thank you for your feedback!',
    });
  };

  return (
    <InlineStack gap="050">
      <Tooltip content="Copy to clipboard" placement="top" isCustom={true}>
        {(context) => (
          <button
            className={styles.actionButton}
            onClick={handleCopyToClipboard}
            aria-label="Copy to clipboard"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            <Icon name="copy-to-clipboard" size="xs" />
          </button>
        )}
      </Tooltip>
      <Tooltip content="This was helpful" placement="top" isCustom={true}>
        {(context) => (
          <button
            className={styles.actionButton}
            onClick={handleThumbsUp}
            aria-label="Thumbs up"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            <Icon name="thumbs-up" size="xs" />
          </button>
        )}
      </Tooltip>
      <Tooltip content="This was not helpful" placement="top" isCustom={true}>
        {(context) => (
          <button
            className={styles.actionButton}
            onClick={handleThumbsDown}
            aria-label="Thumbs down"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            <Icon name="thumbs-down" size="xs" />
          </button>
        )}
      </Tooltip>
    </InlineStack>
  );
}

function Message({ message, isLastMessage = false }: { message: CopilotStatefulMessage; isLastMessage?: boolean }) {
  if (message.role === 'user') {
    return (
      <Box>
        <Text
          py="100"
          px="125"
          backgroundColor="active"
          style={{ borderRadius: themeVars.radius['6'] }}
          display="inline-flex"
          weight="medium"
          maxW="full"
          flexDirection="column"
          gap="100"
        >
          <Markdown>{message.text}</Markdown>
        </Text>
      </Box>
    );
  } else if (message.role === 'model' && message.type === 'text') {
    return (
      <AnimateMessage>
        <Box display="flex" flexDirection="column" gap="025" px="125" className={styles.messageContainerStyle}>
          <Text flexDirection="column" gap="100" display="flex">
            <Markdown>{message.text}</Markdown>
          </Text>
          <Box>
            <Box
              className={clsx(styles.messageActionsContainer, isLastMessage && styles.visibleMessageActionsContainer)}
            >
              <MessageActions text={message.text} />
            </Box>
            {isLastMessage ? (
              <Text size="xxs" color="subtle" display="block">
                AI can make mistakes. Please double-check responses.
              </Text>
            ) : null}
          </Box>
        </Box>
      </AnimateMessage>
    );
  } else if (message.role === 'model' && message.type === 'tool_call') {
    return (
      <AnimateMessage>
        <Box>
          <ToolCalls message={message} />
        </Box>
      </AnimateMessage>
    );
  } else {
    // typescript hack to ensure all cases are handled
    const never: never = message;
    // eslint-disable-next-line no-console
    console.error('Unknown message type', never);
    return null;
  }
}

function ToolCalls({ message }: { message: CopilotStatefulMessage & { type: 'tool_call' } }) {
  const [isOpen, setIsOpen] = useState(false);
  const status = combinedStatus(message.tool_calls);
  const toolCount = message.tool_calls.length;
  const failedCount = message.tool_calls.filter((toolCall) => toolCall.status === 'error').length;
  let label = '';
  if (status === 'executing') {
    label = `Calling ${toolCount} ${plur('tool', toolCount)}`;
  } else if (status === 'error') {
    if (failedCount === toolCount) {
      label = `${toolCount} ${plur('tool', toolCount)} failed`;
    } else {
      label = `Called ${toolCount} ${plur('tool', toolCount)}, ${failedCount} failed`;
    }
  } else {
    label = `Called ${toolCount} ${plur('tool', toolCount)}`;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      overflow="hidden"
      as="details"
      open={isOpen}
      onToggle={() => setIsOpen(!isOpen)}
      className={styles.messageContainerStyle}
    >
      <Box as="summary" display="flex" alignItems="center" gap="050" py="075" cursor="pointer">
        {isOpen ? <Icon name="chevron-down" size="sm" /> : <Icon name="chevron-right" size="sm" />}
        <Text as={status === 'executing' ? ShimmerText : 'span'} color={status === 'executing' ? 'base' : 'subtle'}>
          {label}
        </Text>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        p="075"
        px="100"
        style={{
          border: `1px solid ${themeVars.color['border-minimal']}`,
          borderRadius: themeVars.radius['6'],
        }}
        gap="100"
      >
        {message.tool_calls.map((toolCall, index) => (
          <ToolCall key={index} toolCall={toolCall} />
        ))}
      </Box>
    </Box>
  );
}

function combinedStatus(toolCalls: CopilotStatefulToolCall[]) {
  const statuses = toolCalls.map((toolCall) => toolCall.status);
  if (statuses.includes('executing')) {
    return 'executing';
  } else if (statuses.includes('error')) {
    return 'error';
  } else if (statuses.includes('success')) {
    return 'success';
  }

  return 'success';
}

function ToolCall({ toolCall }: { toolCall: CopilotStatefulToolCall }) {
  return (
    <Box display="flex" gap="075">
      <Box
        style={{ height: 16, width: 16, marginTop: 2 }}
        flexShrink="0"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
      >
        {toolCall.status === 'executing' ? (
          <Spinner style={{ scale: '0.75' }} />
        ) : toolCall.status === 'success' ? (
          <Icon color="success" name="check-circle" size="fill" />
        ) : (
          <Icon color="critical" name="error" size="fill" />
        )}
      </Box>
      <Box display="flex" flexDirection="column" gap="025" flexGrow="1">
        <Text size="xs">{upperFirst(lowerCase(toolCall.name))}</Text>
        {toolCall.status === 'error' ? (
          <Text color="critical" size="xxs">
            {toolCall.response?.error ?? 'An error occurred while calling this tool.'}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

function Input({ session }: { session: CopilotSession }) {
  const [input, setInput] = useState('');
  const lastInput = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status = useCopilotStatus(session);
  const isLoading = status === 'sending' || status === 'receiving';
  const availableTools = useCopilotAvailableTools(session);

  async function submit(input: string) {
    setError(null);
    try {
      await session.sendMessage(input);
    } catch (error) {
      sentryCaptureException(error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  }

  return (
    <>
      {error != null ? (
        <Banner
          title="Network Error"
          variant="error"
          mx="125"
          action={{
            label: 'Retry',
            onClick: () => {
              if (lastInput.current != null) {
                submit(lastInput.current);
              }
            },
          }}
          shouldShowDismissButton={true}
          onClose={() => setError(null)}
        >
          {error}
        </Banner>
      ) : null}
      <Form
        onSubmit={async (e) => {
          e.preventDefault();
          if (input.trim() === '') {
            return;
          }

          if (isLoading) {
            return;
          }

          lastInput.current = input;
          setInput('');
          await submit(input);
        }}
        p="100"
        position="relative"
        autoLoading={false}
      >
        <TextArea
          value={input}
          onInput={(e) => setInput(e.target.value)}
          placeholder="Type your message here..."
          rows={3}
          shouldSubmitOnEnter={true}
          ariaLabel="Send message to Copilot"
          resize="none"
        />
        <Popover shouldTriggerOnHover={true} withArrow={false} placement="top-start" shouldRestrictSize={false}>
          {(context) => (
            <Box
              style={{
                position: 'absolute',
                bottom: themeVars.space['200'],
                left: themeVars.space['200'],
                right: themeVars.space['200'],
              }}
            >
              {availableTools.length === 0 ? null : (
                <Label
                  as="button"
                  cursor="pointer"
                  {...context.reference.getReferenceProps()}
                  ref={context.reference.setReference}
                >
                  {availableTools.length} {plur('Tool', availableTools.length)}
                </Label>
              )}
            </Box>
          )}
          <PopoverContent p="150">
            <Scrollable style={{ maxWidth: '256px', maxHeight: '256px' }} overflow="y" h="full">
              {availableTools.map((tool, index) => (
                <Fragment key={tool.name}>
                  <Box display="flex" flexDirection="column" gap="050">
                    <Text weight="semibold" size="s">
                      {upperFirst(lowerCase(tool.name))}
                    </Text>
                    {tool.description != null ? (
                      <Text color="subtle" size="xs">
                        {tool.description}
                      </Text>
                    ) : null}
                  </Box>
                  {index < availableTools.length - 1 ? <Divider my="200" /> : null}
                </Fragment>
              ))}
            </Scrollable>
          </PopoverContent>
        </Popover>
        <Button
          icon="chevron-up"
          isIconOnly={true}
          variant="primary"
          type="submit"
          size="sm"
          style={{
            position: 'absolute',
            bottom: themeVars.space['200'],
            right: themeVars.space['200'],
          }}
          isLoading={isLoading}
        />
      </Form>
    </>
  );
}

function AnimateMessage({ children }: { children: React.ReactNode }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
      {children}
    </motion.div>
  );
}
